import { getAdminClient } from "@/lib/supabase-admin";
import type { Tables, TablesUpdate, Enums } from "@/lib/supabase.types";

export type OrderRow = Tables<"orders">;
export type OrderItemRow = Tables<"order_items">;
export type OrderStatus = Enums<"order_status">;

const NOK = new Intl.NumberFormat("nb-NO", {
  style: "currency",
  currency: "NOK",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const NB_DT = new Intl.DateTimeFormat("nb-NO", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const NB_DATE_ONLY = new Intl.DateTimeFormat("nb-NO", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** Format øre as NOK with thousand separators. */
export function formatOre(ore: number | null | undefined): string {
  return NOK.format((ore ?? 0) / 100);
}

export function formatOrderDate(iso: string): string {
  return NB_DT.format(new Date(iso));
}

export function formatOrderDateLong(iso: string): string {
  return NB_DATE_ONLY.format(new Date(iso));
}

export const STATUS_LABEL: Record<OrderStatus, string> = {
  PENDING: "Venter på betaling",
  PAID: "Betalt",
  SHIPPED: "Sendt",
  DELIVERED: "Levert",
  CANCELLED: "Avbrutt",
  REFUNDED: "Refundert",
};

/** Filter list of statuses for the admin filter dropdown. PENDING is shown
 *  last as an opt-in for diagnosing abandoned checkouts. */
export const FILTER_STATUSES: OrderStatus[] = [
  "PAID",
  "SHIPPED",
  "DELIVERED",
  "CANCELLED",
  "REFUNDED",
  "PENDING",
];

export async function fetchOrders(status?: OrderStatus): Promise<OrderRow[]> {
  let q = getAdminClient()
    .from("orders")
    .select("*")
    .order("created_at", { ascending: false });
  if (status) {
    q = q.eq("status", status);
  } else {
    // Skjul forlatte PENDING-ordrer fra "Alle"-filteret. De er bare støy
    // (kunde åpnet checkout men fullførte aldri). De vises hvis man velger
    // PENDING eksplisitt i filteret.
    q = q.neq("status", "PENDING");
  }
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export async function fetchOrder(id: string): Promise<OrderRow | null> {
  const { data, error } = await getAdminClient()
    .from("orders")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function fetchOrderItems(orderId: string): Promise<OrderItemRow[]> {
  const { data, error } = await getAdminClient()
    .from("order_items")
    .select("*")
    .eq("order_id", orderId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export interface MarkShippedInput {
  order_id: string;
  tracking_number: string;
  tracking_carrier?: string;
  tracking_url?: string;
}

/** Calls the serverless handler that updates status and sends the customer
 *  shipping email. Pass the current admin session token so the server can
 *  verify the caller is in admin_users. */
export async function markOrderShipped(
  input: MarkShippedInput,
  accessToken: string
): Promise<void> {
  const res = await fetch("/api/order-mark-shipped", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `Kunne ikke markere som sendt (${res.status})`);
  }
}

/** Direct status update through the admin RLS policy. Used for moving
 *  SHIPPED → DELIVERED or other minor transitions where no email needs to
 *  be sent. */
export async function updateOrderStatus(id: string, status: OrderStatus): Promise<void> {
  const patch: TablesUpdate<"orders"> = { status };
  const { error } = await getAdminClient().from("orders").update(patch).eq("id", id);
  if (error) throw error;
}
